import { api } from "../ApiState";
import { IApiResponse } from "../../Types/ApiResponse.model";

export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // list
    getUserList: builder.query<
      IApiResponse<any>,
      { search?: object; page?: number; deleted?: boolean }
    >({
      query: ({ search, page, deleted }) => ({
        url: "user/list",
        method: "GET",
        params: {
          ...(search && { search: JSON.stringify({ ...search }) }),
          ...(page && { page }),
          ...(deleted && { deleted }),
        },
      }),
      providesTags: ["User"],
    }),

    // list of inventory
    getUserListInventory: builder.query<
      IApiResponse<any>,
      { search?: object; page?: number; inventory?: boolean }
    >({
      query: ({ search, page }) => ({
        url: "user/list/by-balance-amount",
        method: "GET",
        params: { ...(page && { page }) },
      }),
      providesTags: ["User"],
    }),

    // show
    showUser: builder.query<any, number>({
      query: (id) => ({
        url: `user/show/${id}`,
        method: "GET",
      }),
      transformResponse(baseQueryReturnValue: IApiResponse) {
        return baseQueryReturnValue?.response?.data;
      },
      providesTags: ["User"],
    }),

    // store
    storeUser: builder.mutation<any, any>({
      query: ({ value }) => ({
        url: "user/store",
        method: "POST",
        body: value,
      }),
      invalidatesTags: (result) => (result ? [{ type: "User" }] : []),
    }),

    // update
    updateUser: builder.mutation<
      IApiResponse<any>,
      { id: number; value: object }
    >({
      query: ({ id, value }) => ({
        url: `user/update/${id}`,
        method: "POST",
        body: { ...value, _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "User" }] : []),
    }),

    // delete
    deleteUser: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `user/delete/${id}`,
        method: "POST",
        body: { _method: "DELETE" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "User" }] : []),
    }),

    // pending
    uploadUserStatus: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `user/change-status-to-upload/${id}`,
        method: "POST",
        body: { _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "User" }] : []),
    }),

    // get agent list
    userAgentList: builder.query<
      IApiResponse<any>,
      { id: number; page: number }
    >({
      query: ({ id, page }) => ({
        url: `user/list-of-auth-log/${id}`,
        method: "GET",
        params: { page: page },
      }),
      providesTags: ["Agent"],
    }),

    // delete agent
    deleteUserAgent: builder.mutation<
      IApiResponse<any>,
      { userId: number; agentId: number }
    >({
      query: ({ userId, agentId }) => ({
        url: `user/auth-log/${userId}/${agentId}`,
        method: "POST",
        body: { _method: "DELETE" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "Agent" }] : []),
    }),

    // two step verify
    twoStepVerify: builder.mutation<
      IApiResponse<any>,
      {
        id: number;
        password: string;
        repeatPassword: string;
        isActive: boolean;
        personality: string;
      }
    >({
      query: ({ id, password, repeatPassword, isActive, personality }) => ({
        url: `${personality}/set-two-verify/${id}`,
        method: "POST",
        body: {
          password: password ?? undefined,
          repeat_password:
            password && repeatPassword ? repeatPassword : undefined,
          is_active: isActive,
          _method: "PUT",
        },
      }),
      invalidatesTags: (result) => (result ? [{ type: "User" }] : []),
    }),

    // show user document status
    showUserDocument: builder.query<any, number>({
      query: (id) => ({
        url: `user/show-document-status/${id}`,
        method: "GET",
      }),
      transformResponse(baseQueryReturnValue: IApiResponse) {
        return baseQueryReturnValue?.response?.data;
      },
      providesTags: ["UserDocument"],
    }),

    // update user document status
    updateUserDocument: builder.mutation<
      IApiResponse<any>,
      { id: number; status: "verified" | "unverified" }
    >({
      query: ({ id, status }) => ({
        url: `user/change-document-status/${id}`,
        method: "POST",
        body: { status: status, _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "UserDocument" }] : []),
    }),

    // get new secret key
    getSecretKey: builder.query<any, number>({
      query: (id) => ({
        url: `user/secret-key/${id}`,
        method: "GET",
      }),
      transformResponse(baseQueryReturnValue: IApiResponse) {
        return baseQueryReturnValue?.response?.data;
      },
    }),

    // notification setting
    userNotificationSetting: builder.mutation<any, number>({
      query: (id) => ({
        url: "notification/show",
        method: "POST",
        body: { user_id: id },
      }),
    }),

    // telegram id setting
    userStoreTelegramId: builder.mutation<
      any,
      { baseId: number; telegramId: string; type: "user" | "gateway" }
    >({
      query: ({ baseId, telegramId, type }) => ({
        url: "notification/set-telegram-id",
        method: "POST",
        body:
          type === "user"
            ? { telegram_id: telegramId, user_id: baseId }
            : { telegram_id: telegramId, gateway_id: baseId },
      }),
      invalidatesTags: (result) => (result ? [{ type: "User" }] : []),
    }),

    // notification setting
    getNotificationSetting: builder.mutation<
      any,
      { gateway_id?: number; user_id?: number }
    >({
      query: (data) => ({
        url: "notification/show",
        method: "POST",
        body: data,
      }),

      transformResponse(baseQueryReturnValue: IApiResponse) {
        return baseQueryReturnValue?.response?.data;
      },
    }),

    // set new notification setting
    storeNotification: builder.mutation<
      any,
      { setting: any; gateway_id?: number; user_id?: number }
    >({
      query: (data) => ({
        url: "notification/set-setting",
        method: "POST",
        body: data,
      }),
    }),

    // status
    acceptUserStatus: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `user/change-status-to-accept/${id}`,
        method: "POST",
        body: { _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "UserDocument" }] : []),
    }),

    rejectUserStatus: builder.mutation<
      IApiResponse<any>,
      { id: number; comment: any }
    >({
      query: ({ id, comment }) => ({
        url: `user/change-status-to-reject/${id}`,
        method: "POST",
        body: { _method: "PUT", comment: comment },
      }),
      invalidatesTags: (result) => (result ? [{ type: "UserDocument" }] : []),
    }),

    banUserStatus: builder.mutation<IApiResponse<any>, number>({
      query: (id) => ({
        url: `user/change-status-to-ban/${id}`,
        method: "POST",
        body: { _method: "PUT" },
      }),
      invalidatesTags: (result) => (result ? [{ type: "User" }] : []),
    }),
  }),
});

export const {
  useGetUserListQuery,
  useGetUserListInventoryQuery,
  useShowUserQuery,
  useStoreUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useUploadUserStatusMutation,
  useUserAgentListQuery,
  useDeleteUserAgentMutation,
  useTwoStepVerifyMutation,
  useShowUserDocumentQuery,
  useUpdateUserDocumentMutation,
  useGetSecretKeyQuery,
  useUserNotificationSettingMutation,
  useUserStoreTelegramIdMutation,
  useGetNotificationSettingMutation,
  useStoreNotificationMutation,
  useAcceptUserStatusMutation,
  useRejectUserStatusMutation,
  useBanUserStatusMutation,
} = userApi;

export default userApi;
